import { Body } from "@hypercard/web-components";
import * as z from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { CreateStipendStep } from "./types";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  StipendCategory,
  useAdminDepartmentsQuery,
} from "@common/utils/graphql";
import {
  DepartmentOption,
  SelectDepartment,
} from "@common/components/SelectDepartment";
import { Card } from "@/components/ui/card";
import { useRecoilState } from "recoil";
import { createOrEditStipendState } from "@common/state/createOrEditStipendState";
import { AnimatedProps, animated } from "@react-spring/web";
import { CSSProperties } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getCategoryDisplay } from "../../util";

const categories = [
  StipendCategory.Wellness,
  StipendCategory.Food,
  StipendCategory.Travel,
  StipendCategory.Commute,
  StipendCategory.Fitness,
  StipendCategory.Network,
  StipendCategory.Education,
  StipendCategory.Healthcare,
  StipendCategory.Celebration,
  StipendCategory.Convenience,
  StipendCategory.Connectivity,
  StipendCategory.Entertainment,
];
const BaseFormSchema = z.object({
  name: z.string().nonempty("Name is required"),
  longDetails: z.string().nonempty("Description is required"),
  departments: z
    .array(
      z.object({
        name: z.string(),
        id: z.number().nullable(),
      }),
    )
    .nonempty("At least one department must be selected"),
  category: z.enum(
    [
      StipendCategory.Wellness,
      StipendCategory.Food,
      StipendCategory.Travel,
      StipendCategory.Commute,
      StipendCategory.Fitness,
      StipendCategory.Network,
      StipendCategory.Education,
      StipendCategory.Healthcare,
      StipendCategory.Celebration,
      StipendCategory.Convenience,
      StipendCategory.Connectivity,
      StipendCategory.Entertainment,
    ],
    { required_error: "Category is required" },
  ),
});

type BaseFormValues = z.infer<typeof BaseFormSchema>;

type Props = AnimatedProps<{
  onNext: (step: CreateStipendStep) => void;
  onBack: () => void;
  style: CSSProperties;
  isEditing: boolean;
}>;

export const BaseForm = ({ onNext, onBack, style, isEditing }: Props) => {
  const { data: departmentsData } = useAdminDepartmentsQuery();
  const [
    { name, longDetails, departments, category },
    setCreateOrEditStipendState,
  ] = useRecoilState(createOrEditStipendState);

  const form = useForm<BaseFormValues>({
    resolver: zodResolver(BaseFormSchema),
    defaultValues: {
      name,
      longDetails,
      departments,
      category,
    },
  });

  const onSubmit = (data: BaseFormValues) => {
    setCreateOrEditStipendState((prevState) => ({
      ...prevState,
      ...data,
    }));
    onNext(CreateStipendStep.DetailsForm);
  };

  const departmentOptions: DepartmentOption[] =
    departmentsData?.departments?.__typename === "QueryDepartmentsSuccess"
      ? departmentsData.departments.data.departments
      : [];

  return (
    <animated.div
      style={style}
      className="mt-6 flex w-full grow flex-col pl-6 lg:pl-10"
    >
      <Body size="2xl" weight="medium">
        {isEditing ? "Edit Stipend" : "New Stipend"}
      </Body>
      <Body size="md" weight="medium" className="mt-3 text-white/70">
        First, let's define stipend name and description.
      </Body>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="mt-6 flex grow flex-col"
        >
          <div className="flex flex-col gap-10 lg:flex-row">
            <div className="flex-1 space-y-8">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Stipend Name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="longDetails"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Stipend Description"
                        className="resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="departments"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Departments</FormLabel>
                    <FormControl>
                      <SelectDepartment
                        {...field}
                        options={departmentOptions}
                        setValue={(value) => {
                          if (typeof value === "function") {
                            field.onChange(value(field.value));
                          } else {
                            field.onChange(value);
                          }
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category</FormLabel>
                    <FormControl>
                      <Select
                        value={field.value}
                        onValueChange={(val: StipendCategory) => {
                          field.onChange(val);
                        }}
                      >
                        <SelectTrigger className="w-40">
                          <SelectValue placeholder="Select a Category" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((cat) => (
                            <SelectItem
                              value={cat}
                              key={`categories--category-${cat}`}
                            >
                              {getCategoryDisplay(cat)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex-1">
              <Card className="space-y-4 p-6">
                <Body size="md" weight="medium">
                  Be as descriptive as possible
                </Body>
                <Body size="sm" weight="regular" className="text-white/70">
                  To ensure that employees claim stipends correctly, please
                  review the stipend name and description carefully. This will
                  help prevent any misunderstandings or incorrect claims.
                </Body>
              </Card>
            </div>
          </div>
          <div className="mt-10 flex flex-grow items-end justify-between">
            <Button type="button" variant="ghost" onClick={onBack}>
              Back
            </Button>
            <Button type="submit" className="w-44">
              Next
            </Button>
          </div>
        </form>
      </Form>
    </animated.div>
  );
};
